import { useEffect, useMemo, useState } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Meta from '../components/Meta';
import HomeFooter from '../components/HomeFooter';
import PageHeader from '../components/shell/PageHeader';
import ShowGrid from '../components/ShowGrid';
import { OrgCard, UserCard } from '../components/ProfileCards';
import { Link, useRouter } from '../util/router';
import { useAuth } from '../util/auth';
import { isPastShow } from '../util/data-factory';
import { SearchCategory, fullTextSearch, getNeighborhoodsByOrgIds } from '../util/db';
import showrunnerLogo from '../assets/images/SHOWRUNNER.png';

const CATEGORIES = [
  { value: SearchCategory.SHOW, label: 'Show' },
  { value: SearchCategory.ARTIST, label: 'Artist' },
  { value: SearchCategory.VENUE, label: 'Venue' },
  { value: SearchCategory.COMMUNITY, label: 'Community' },
];

const EMPTY_COPY = {
  [SearchCategory.SHOW]: ['Search shows by title', 'No shows found'],
  [SearchCategory.ARTIST]: ['Search shows by artist name', 'No artists found'],
  [SearchCategory.VENUE]: ['Search galleries, museums and institutions', 'No venues found'],
  [SearchCategory.COMMUNITY]: ['Find people on Showrunner by username', 'No people found'],
};

function useDebounced(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function SearchPage() {
  const auth = useAuth();
  const router = useRouter();

  const urlQuery = typeof router.query.q === 'string' ? router.query.q : '';
  const urlType = Object.values(SearchCategory).includes(router.query.type)
    ? router.query.type
    : SearchCategory.SHOW;

  const [rawQuery, setRawQuery] = useState(urlQuery);
  const query = useDebounced(rawQuery.trim(), 400);
  const category = urlType;
  const needsSignin = category === SearchCategory.COMMUNITY && !auth.user;

  const [results, setResults] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState(null);

  const setParams = (q, type) => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (type && type !== SearchCategory.SHOW) params.set('type', type);
    const search = params.toString();
    router.replace(`/search${search ? `?${search}` : ''}`);
  };

  useEffect(() => {
    if (query !== urlQuery) setParams(query, category);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    if (!query || needsSignin) {
      setResults([]);
      return undefined;
    }
    (async () => {
      setIsFetching(true);
      setError(null);
      try {
        let data = await fullTextSearch(query, category);
        if (category === SearchCategory.VENUE) data = await getNeighborhoodsByOrgIds(data);
        if (!cancelled) setResults(data ?? []);
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          setResults([]);
          setError(
            error?.code === '57014'
              ? 'Search took too long. Try a longer or more specific term.'
              : 'Search failed. Please try again.'
          );
        }
      } finally {
        if (!cancelled) setIsFetching(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [query, category, needsSignin]);

  const selectCategory = value => {
    setResults([]);
    setParams(query, value);
  };

  const { liveShows, pastShows } = useMemo(() => {
    if (category !== SearchCategory.SHOW && category !== SearchCategory.ARTIST) {
      return { liveShows: [], pastShows: [] };
    }
    const live = [];
    const past = [];
    results.forEach(show => (show.end_date && isPastShow(show.end_date) ? past : live).push(show));
    live.sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
    past.sort((a, b) => new Date(b.end_date) - new Date(a.end_date));
    return { liveShows: live, pastShows: past };
  }, [results, category]);

  const [promptCopy, emptyCopy] = EMPTY_COPY[category];
  const hasResults = results.length > 0;

  return (
    <>
      <Meta title="Search - Showrunner" />
      <PageHeader title="Search">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-6">
          <label className="flex h-11 flex-1 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 md:h-12 md:max-w-2xl">
            <MagnifyingGlassIcon className="h-5 w-5 shrink-0 text-slate-500" />
            <input
              type="search"
              autoFocus
              value={rawQuery}
              onChange={e => setRawQuery(e.target.value)}
              placeholder="Search shows, artists, venues, people"
              className="h-full w-full border-0 bg-transparent font-oswald text-base outline-none focus:ring-0"
              aria-label="Search"
            />
            {rawQuery && (
              <button type="button" onClick={() => setRawQuery('')} aria-label="Clear search">
                <XMarkIcon className="h-5 w-5 text-slate-500" />
              </button>
            )}
          </label>
          <div className="no-scrollbar flex gap-2 overflow-x-auto" role="tablist">
            {CATEGORIES.map(c => (
              <button
                key={c.value}
                type="button"
                role="tab"
                aria-selected={category === c.value}
                onClick={() => selectCategory(c.value)}
                className={`shrink-0 rounded-lg px-4 py-1.5 text-sm font-bold transition-colors ${
                  category === c.value
                    ? 'bg-secondary-red text-white'
                    : 'bg-white text-slate-900 hover:bg-slate-200'
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
      </PageHeader>

      <section className="mx-auto min-h-[60vh] w-full max-w-[90rem] px-4 py-6 md:px-8 md:py-10">
        {isFetching && !hasResults && (
          <div className="flex justify-center py-16">
            <span className="loading loading-spinner loading-lg text-secondary-red" />
          </div>
        )}

        {needsSignin && (
          <div className="flex flex-col items-center py-16 text-center">
            <img src={showrunnerLogo} alt="" aria-hidden className="mb-6 h-24 w-24 opacity-10" />
            <p className="text-lg text-slate-600">Sign in to find people on Showrunner.</p>
            <Link
              to={`/auth/signin?next=${encodeURIComponent(
                `/search?type=community${query ? `&q=${encodeURIComponent(query)}` : ''}`
              )}`}
              className="btn btn-sm mt-4 bg-secondary-red text-white"
            >
              Sign in
            </Link>
          </div>
        )}

        {!needsSignin && !isFetching && !hasResults && (
          <div className="flex flex-col items-center py-16 text-center">
            <img src={showrunnerLogo} alt="" aria-hidden className="mb-6 h-24 w-24 opacity-10" />
            <p className="text-lg text-slate-600">
              {error ?? (query ? emptyCopy : promptCopy)}
            </p>
          </div>
        )}

        {hasResults && category === SearchCategory.VENUE && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {results.map(org => (
              <OrgCard key={org.org_id} org={org} />
            ))}
          </div>
        )}

        {hasResults && category === SearchCategory.COMMUNITY && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {results.map(user => (
              <UserCard key={user.id || user.username} user={user} />
            ))}
          </div>
        )}

        {liveShows.length > 0 && (
          <div className="mb-12">
            <h2 className="mb-4 text-xl font-semibold capitalize text-slate-700 md:text-2xl">
              Live & upcoming shows
            </h2>
            <ShowGrid shows={liveShows} source="search" />
          </div>
        )}
        {pastShows.length > 0 && (
          <div>
            <h2 className="mb-4 text-xl font-semibold capitalize text-slate-700 md:text-2xl">
              Past shows
            </h2>
            <ShowGrid shows={pastShows} source="search" />
          </div>
        )}
      </section>
      <HomeFooter />
    </>
  );
}

export default SearchPage;
